
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { AffiliateModule, ApplicationModule, WizardModule } from '@/store'
import { FullApplicationService, SegmentService, PartialApplicationService } from '@/services'

import IWizard from './IWizard'
import WizardStep from './WizardStep'
import { IAppForm } from '../forms/IAppForm'

import Modal from '@/components/shared/Modal.vue'
import ProgressBar from '@/components/shared/ProgressBar.vue'
import TermsAndConditions from '@/components/shared/TermsAndConditions.vue'
import { WizardPage_Enum } from '../../enums/WizardPage_Enum'

@Component({
  name: 'Wizard',
  components: {
    ProgressBar,
    Modal,
    TermsAndConditions
  }
})
export default class Wizard extends Vue implements IWizard {
  // #region  Props

  @Prop() steps!: WizardStep[];
  @Prop({ default: false }) showNav!: boolean;
  @Prop({ default: false }) showButtons!: boolean;

  // #endregion

  // #region Data

  submitting = false;
  page = 9;
  prevPage = 10;

  show = false;
  showTerms = false;

  // #endregion

  // #region Methods

  mounted () {
    ApplicationModule.isPrePopped ? this.goToPage(WizardPage_Enum.Summary) : null

    setTimeout(() => {
      this.show = true
    }, 400)

    setTimeout( ()=>{
			const nav = (this.$refs.nav as Element);

			if (nav && nav.clientHeight) {

				document.documentElement.style.cssText = `--screenHeigh: ${(document.getElementById('app') as HTMLElement).offsetHeight}px; 
                        --bottomHeigh: ${nav.clientHeight}px;
                        --screenWidth: ${window.innerWidth}px;
                        --bannerNegativeMarginLeft: -${(window.innerWidth- (document.getElementById('app') as HTMLElement).offsetWidth) / 2}px;`;
			}

      const reportWindowSize = () => {
				if (nav.clientHeight) {
					document.documentElement.style.cssText = `--screenHeigh: ${(document.getElementById('app') as HTMLElement).offsetHeight}px; 
                              --bottomHeigh: ${nav.clientHeight}px;
                              --screenWidth: ${window.innerWidth}px;
                              --bannerNegativeMarginLeft: -${(window.innerWidth- (document.getElementById('app') as HTMLElement).offsetWidth) / 2}px;`;
				}
      }

      window.onresize = reportWindowSize;

    }, 800)
  }

  validateAllSteps () {
    this.steps.forEach((s) => {
      // Get the current component type as string
      const comp = s.component

      if (comp === 'Summary') {
        return
      }
      // Cast the dynamic component as IAppForm and call the validate method
      const isValid = ((this.$refs[comp] as unknown[])[0] as IAppForm).validate()

      s.isComplete = isValid
    })

    WizardModule.updateWizardSteps(this.steps)
  }

  sendFullApp () {
    FullApplicationService.sendFullApp((this as any).$appInsights)
  }

  submit (wizardIndex: number) {
    // Get the current component type as string
    const comp = this.steps[wizardIndex - 1].component

    // Cast the dynamic component as IAppForm and call the validate method
    const isValid = ((this.$refs[comp] as unknown[])[0] as IAppForm).validate()

    if (!isValid) {
      return
    }

    // set surrent step to complete
    this.steps[wizardIndex - 1].isComplete = true

    if (WizardModule.callback) {
      WizardModule.updateCallback()
      this.goToPage(WizardPage_Enum.Summary)
      return false
    }

    this.next()
    return true
  }

  next () {
		PartialApplicationService.sendPartialApp((this as any).$appInsights)
    this.prevPage = JSON.parse(JSON.stringify(this.page))
    this.page++
  }

  previous () {
    if (WizardModule.callback) {
      WizardModule.updateCallback(false)
      this.goToPage(WizardPage_Enum.Summary)

      return
    }

    this.prevPage = JSON.parse(JSON.stringify(this.page))
    this.page--
  }

  goToPage (val: WizardPage_Enum) {
    if( window && window.scroll) {
      window.scroll({top:0, left: 0, behavior: "instant"} as any)
    }

    if (val === WizardPage_Enum.Summary) {
      WizardModule.updateCallback()
    }

		PartialApplicationService.sendPartialApp((this as any).$appInsights)

    this.prevPage = JSON.parse(JSON.stringify(this.page))
    this.page = Number(val)

    setTimeout(() => {
        let title = WizardPage_Enum[Number(val)].toString()

				SegmentService.trackPage(ApplicationModule.GUID, title)

        // use this to silently update URL without triggering route change in Vue Router
        history.pushState(
            {},
            title,
            `${window.location.origin}/#${title}`
        )

        
    }, 50)
  }

  showTandC () {
    this.showTerms = true
  }

  agreeToTerms () {
    const form = ApplicationModule.form
    form.agreedToTermsAndConditions = true
    ApplicationModule.updateForm(form)
    this.showTerms = false
  }

  // #region Computed

  get imgUrl () {
    return require('@/assets' + this.logoPath)
  }

  get logoPath () {
    return AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.logoPath ? AffiliateModule.affiliateConfig.logoPath : ''
  }

  get logoSizeObj () {
    return AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.logoSize ? AffiliateModule.affiliateConfig.logoSize : {}
  }

  get logoAlt () {
    return AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.logoPath ? AffiliateModule.affiliateConfig.displayName + ' logo' : ''
  }

	get swapLogo () {
		return AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.swapLogo && AffiliateModule.affiliateConfig.swapLogo === true
	}

	get powerbyLogo () {
		return AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.powerbyLogo && AffiliateModule.affiliateConfig.powerbyLogo === true
	}

  get hasCallback () {
    return WizardModule.callback
  }

  get isPrePopped () {
    return ApplicationModule.isPrePopped
  }

  get currentStep () {
    return this.steps[this.page - 1]
  }

  get overrideShowButtons () {
    return this.currentStep.showButtons
  }

  get progressPercent () {
    if (this.steps.length === 0 || this.page === 1) {
      return 0
    }

    return (this.page - 1) / this.steps.length
  }

  get validatedPercent () {
    return this.steps ? (this.steps.filter(s => s.isComplete).length / this.steps.length) : 0
  }

  get isFinalStep () {
    return this.page === this.steps.length
  }

  get affiliateName () {
    return AffiliateModule.affiliateConfig ? AffiliateModule.affiliateConfig.displayName : ''
  }

  get getBanner () {
    if( AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.showBanner 
          && AffiliateModule.affiliateConfig.bannerMessage 
          && AffiliateModule.affiliateConfig.bannerColor 
    ) {
      return { showBanner: AffiliateModule.affiliateConfig.showBanner, 
              bannerMessage:  AffiliateModule.affiliateConfig.bannerMessage,
              bannerColor:  AffiliateModule.affiliateConfig.bannerColor
            }
    }
    return { showBanner: false, bannerMessage: null, bannerColor: null };
  }

  get isBanner () {
    if( AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.showBanner  ) {
      return AffiliateModule.affiliateConfig.showBanner 
    }
    return false
  }

  get previousButtonText () {
    return this.page === 1 ? '' : this.steps[this.page - 2].name
  }

  get route () {
    return this.$route.hash
  }

	@Watch('route')
  backOverride () {
    if (this.route === '#Summary' || this.route === '') {
      this.goToPage(WizardPage_Enum.Summary)
    }
  }
  // #endregion
}
